import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import {
    getEffectiveDate,
    getRenewalDate,
    getSortedPastPaidPayments,
    isPolicyCancelled
} from 'common/source/utilities/policyInfoHelper';
import { formatAsDollarsWithoutZeroCentsOrEmptyString } from 'common/source/utilities/strings';

import * as _ from 'lodash';
import * as moment from 'moment';
import { IAnchorScrollService, ITimeoutService } from 'angular';
import {
    AMERICAN_DATE_FORMAT,
    getMomentFromHippoDateString,
    isTodayBetween20And30DaysAfterDate,
    isTodayWithinFiveDaysBeforeDate
} from 'common/source/utilities/dates';
import {
    ADDITIONAL_INFORMATION_MAP,
    CUSTOMER_PAYMENT_STATUS,
    paymentMethodDisplayName,
    pendingEscrowTooltipContent
} from './enums';

const module = angular.module('payment-status-breakdown', []);

interface PaymentStatusBreakdownItem {
    date: string | undefined;
    value: string;
    pastDue: boolean;
}

interface PaymentStatusBreakdownController extends angular.IStrictController {
    canOnlyPayLastFailedPaymentOption: boolean;
    additionalInformationText: string;
    paymentStatusLabel: string;
    paymentAmount: string;
    paymentFrequency: string;
    policyInfo: CustomerPolicyInfo;
    showAutoRenewal: boolean;
    cancellationDate?: string;
    renewalDate: string;
    paymentStatusBreakdownItem: PaymentStatusBreakdownItem;
    paymentMethod: string;
    showPayNowBasedOnStatus: boolean;
    isEscrowSelected: () => boolean;
    isCreditCardSelected: () => boolean;
    isAchSelected: () => boolean;
    tooltipContent: string;
}

enum PAYMENT_STATUS_TYPES {
    PAID,
    PENDING,
    ERROR,
    REQUIRED
}

const WITH_PAYMENT_ERROR_CLASS = 'with-payment-error';
const WITH_PAYMENT_PENDING_CLASS = 'with-payment-pending';
const WITH_PAYMENT_SUCCESS_CLASS = 'with-payment-success';
const WITH_PAYMENT_REQUIRED_CLASS = 'with-payment-required';

const PAYMENT_STATUSES_THAT_SHOW_RENEWAL_INFO: Array<PaymentStatus> = [
    CUSTOMER_PAYMENT_STATUS.PENDING,
    CUSTOMER_PAYMENT_STATUS.PAID,
    CUSTOMER_PAYMENT_STATUS.DELAYED,
    CUSTOMER_PAYMENT_STATUS.CC_PAYMENTS_REQUIRED
];

module.component('paymentStatusBreakdown', {
    bindings: {
        policyInfo: '<',
        isEditMode: '<'
    },
    template: template,
    // tslint:disable-next-line
    controller: function(
        this: PaymentStatusBreakdownController,
        $element: angular.IAugmentedJQuery,
        $anchorScroll: IAnchorScrollService,
        $timeout: ITimeoutService
    ) {
        'ngInject';

        const setPaymentStatus = (statusLabel: string, statusType: PAYMENT_STATUS_TYPES) => {
            // Clear previous status.
            $element
                .removeClass(WITH_PAYMENT_ERROR_CLASS)
                .removeClass(WITH_PAYMENT_PENDING_CLASS)
                .removeClass(WITH_PAYMENT_SUCCESS_CLASS)
                .removeClass(WITH_PAYMENT_REQUIRED_CLASS);

            if (PAYMENT_STATUS_TYPES.ERROR === statusType) {
                $element.addClass(WITH_PAYMENT_ERROR_CLASS);
            } else if (PAYMENT_STATUS_TYPES.PENDING === statusType) {
                $element.addClass(WITH_PAYMENT_PENDING_CLASS);
            } else if (PAYMENT_STATUS_TYPES.PAID === statusType) {
                $element.addClass(WITH_PAYMENT_SUCCESS_CLASS);
            } else if (PAYMENT_STATUS_TYPES.REQUIRED === statusType) {
                $element.addClass(WITH_PAYMENT_REQUIRED_CLASS);
            } else {
                throw new Error('payment type not supported');
            }

            this.paymentStatusLabel = statusLabel;
        };

        // tslint:disable-next-line
        const setPaymentStatusAndBreakdownItemsAndAdditionalInformation = () => {
            if (
                !isPolicyCancelled(this.policyInfo) &&
                !this.policyInfo.pending_terminate &&
                _.includes(PAYMENT_STATUSES_THAT_SHOW_RENEWAL_INFO, this.policyInfo.payment_status)
            ) {
                this.showAutoRenewal = true;
                this.renewalDate = getRenewalDate(this.policyInfo);
            }

            if (
                this.policyInfo.pending_terminate &&
                this.policyInfo.payment_status !== CUSTOMER_PAYMENT_STATUS.FAILED &&
                this.policyInfo.payment_status !== CUSTOMER_PAYMENT_STATUS.NOT_RECEIVED
            ) {
                setPaymentStatus('Pending Cancellation', PAYMENT_STATUS_TYPES.ERROR);
                this.cancellationDate = this.policyInfo.update_date;
                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PENDING_TERMINATE_OTHER;
                this.showAutoRenewal = false;
                this.renewalDate = '';
                return;
            }

            if (this.policyInfo.payment_amount > 0) {
                this.paymentAmount = formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount);
            }
            const sortedPastPaidPayments = getSortedPastPaidPayments(this.policyInfo);
            const mostRecentPaidPayment = sortedPastPaidPayments[0];

            // TODO: Add ach payment failed message
            switch (this.policyInfo.payment_status) {
                case CUSTOMER_PAYMENT_STATUS.CC_PAYMENTS_REQUIRED:
                    setPaymentStatus('Outstanding Balance', PAYMENT_STATUS_TYPES.REQUIRED);
                    this.paymentStatusBreakdownItem = {
                        value: formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.remaining_payment_amount),
                        date: this.policyInfo.payment_due_date,
                        pastDue: true
                    };
                    this.paymentAmount = formatAsDollarsWithoutZeroCentsOrEmptyString(
                        this.policyInfo.remaining_payment_amount
                    );
                    this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.CC_PAYMENTS_REQUIRED;
                    this.showPayNowBasedOnStatus = true;
                    break;
                case CUSTOMER_PAYMENT_STATUS.PAID:
                    if (!this.policyInfo.payment_due_date) {
                        return;
                    }

                    if (
                        mostRecentPaidPayment &&
                        mostRecentPaidPayment.date &&
                        mostRecentPaidPayment.sum &&
                        this.policyInfo.next_unpaid_payment_amount > 0 &&
                        !!this.policyInfo.next_unpaid_payment_due_date
                    ) {
                        this.paymentStatusBreakdownItem = {
                            date: this.policyInfo.next_unpaid_payment_due_date,
                            value: formatAsDollarsWithoutZeroCentsOrEmptyString(
                                this.policyInfo.next_unpaid_payment_amount
                            ),
                            pastDue: false
                        };
                        if (isTodayWithinFiveDaysBeforeDate(this.policyInfo.next_unpaid_payment_due_date)) {
                            setPaymentStatus(`Upcoming Payment`, PAYMENT_STATUS_TYPES.PENDING);

                            if (this.paymentMethod === paymentMethodDisplayName.cc) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.UPCOMING_PAYMENT.CC(
                                    this.policyInfo.next_unpaid_payment_due_date,
                                    formatAsDollarsWithoutZeroCentsOrEmptyString(
                                        this.policyInfo.next_unpaid_payment_amount
                                    )
                                );
                            }

                            if (this.paymentMethod === paymentMethodDisplayName.ach) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.UPCOMING_PAYMENT.ACH(
                                    this.policyInfo.next_unpaid_payment_due_date,
                                    formatAsDollarsWithoutZeroCentsOrEmptyString(
                                        this.policyInfo.next_unpaid_payment_amount
                                    )
                                );
                            }
                            this.showPayNowBasedOnStatus = true;
                        } else {
                            setPaymentStatus(`Paid on ${mostRecentPaidPayment.date}`, PAYMENT_STATUS_TYPES.PAID);
                            this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PAID;
                        }
                    } else {
                        setPaymentStatus(`Paid in Full ${this.policyInfo.payment_due_date}`, PAYMENT_STATUS_TYPES.PAID);
                        this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PAID;
                    }
                    break;
                case CUSTOMER_PAYMENT_STATUS.DELAYED:
                    setPaymentStatus(`Upcoming Payment`, PAYMENT_STATUS_TYPES.PENDING);
                    this.paymentStatusBreakdownItem = {
                        date: this.policyInfo.payment_due_date,
                        value: formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount),
                        pastDue: false
                    };
                    this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.DELAYED(
                        formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount)
                    );
                    break;
                case CUSTOMER_PAYMENT_STATUS.NOT_RECEIVED_POLICY_CANCELLED:
                    setPaymentStatus('Payment not received (Policy Cancelled)', PAYMENT_STATUS_TYPES.ERROR);

                    if (this.paymentMethod === paymentMethodDisplayName.escrow) {
                        this.additionalInformationText =
                            ADDITIONAL_INFORMATION_MAP.NOT_RECEIVED_POLICY_CANCELLED.ESCROW;
                    }

                    if (
                        this.paymentMethod === paymentMethodDisplayName.ach ||
                        this.paymentMethod === paymentMethodDisplayName.cc
                    ) {
                        this.additionalInformationText =
                            ADDITIONAL_INFORMATION_MAP.NOT_RECEIVED_POLICY_CANCELLED.ACH_OR_CC;
                    }

                    this.cancellationDate = this.policyInfo.update_date;
                    break;
                case CUSTOMER_PAYMENT_STATUS.PENDING:
                    if (this.paymentMethod === paymentMethodDisplayName.escrow) {
                        setPaymentStatus(`Pending Payment`, PAYMENT_STATUS_TYPES.PENDING);

                        if (isTodayBetween20And30DaysAfterDate(this.policyInfo.effective_date)) {
                            this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PENDING.ESCROW.BETWEEN_20_AND_30_DAYS_AFTER_EFFECTIVE_DATE(
                                moment(this.policyInfo.effective_date)
                                    .add(45, 'days')
                                    .format('L')
                            );
                        } else {
                            this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PENDING.ESCROW.UP_TO_20_DAYS_AFTER_EFFECTIVE_DATE(
                                moment(this.policyInfo.effective_date)
                                    .add(30, 'days')
                                    .format('L')
                            );
                        }
                        this.tooltipContent = pendingEscrowTooltipContent;
                    } else {
                        // show upcoming payment payment status when payment is due within the next five days
                        if (
                            this.policyInfo.payment_due_date &&
                            isTodayWithinFiveDaysBeforeDate(this.policyInfo.payment_due_date)
                        ) {
                            setPaymentStatus(`Upcoming Payment`, PAYMENT_STATUS_TYPES.PENDING);

                            if (this.paymentMethod === paymentMethodDisplayName.cc) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.UPCOMING_PAYMENT.CC(
                                    this.policyInfo.payment_due_date,
                                    formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount)
                                );
                            }

                            if (this.paymentMethod === paymentMethodDisplayName.ach) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.UPCOMING_PAYMENT.ACH(
                                    this.policyInfo.payment_due_date,
                                    formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount)
                                );
                            }
                            this.showPayNowBasedOnStatus = true;
                        } else {
                            // if payment is not due within the next five days, show pending status
                            setPaymentStatus(`Pending Payment`, PAYMENT_STATUS_TYPES.PENDING);

                            if (this.paymentMethod === paymentMethodDisplayName.cc) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PENDING.CC(
                                    getEffectiveDate(this.policyInfo)
                                );
                            } else if (this.paymentMethod === paymentMethodDisplayName.ach) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.PENDING.ACH(
                                    getEffectiveDate(this.policyInfo)
                                );
                            }
                        }
                    }

                    this.paymentStatusBreakdownItem = {
                        date: this.policyInfo.payment_due_date,
                        value: formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount),
                        pastDue: false
                    };
                    break;
                case CUSTOMER_PAYMENT_STATUS.OUTSTANDING:
                    setPaymentStatus('Outstanding Balance', PAYMENT_STATUS_TYPES.ERROR);

                    this.paymentStatusBreakdownItem = {
                        date: this.policyInfo.payment_due_date,
                        value: formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount),
                        pastDue: true
                    };

                    this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.OUTSTANDING(
                        formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount)
                    );

                    this.cancellationDate = this.policyInfo.update_date;
                    this.showPayNowBasedOnStatus = true;
                    break;
                case CUSTOMER_PAYMENT_STATUS.FAILED:
                    if (isPolicyCancelled(this.policyInfo)) {
                        setPaymentStatus('Payment Failed (Policy Cancelled)', PAYMENT_STATUS_TYPES.ERROR);
                        this.additionalInformationText =
                            ADDITIONAL_INFORMATION_MAP.NOT_RECEIVED_POLICY_CANCELLED.ACH_OR_CC;
                        this.cancellationDate = this.policyInfo.update_date;
                    } else if (this.policyInfo.pending_terminate) {
                        setPaymentStatus('Payment Failed (Pending Cancellation)', PAYMENT_STATUS_TYPES.ERROR);
                        // for policies that have a non-payment cancellation reason, present the last failed payment info
                        // which is sent as next unpaid payment amount
                        if (
                            this.policyInfo.next_unpaid_payment_amount &&
                            this.policyInfo.next_unpaid_payment_due_date
                        ) {
                            if (this.paymentMethod === paymentMethodDisplayName.cc) {
                                this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.FAILED_PENDING_TERMINATE.CC;
                            }

                            if (this.paymentMethod === paymentMethodDisplayName.ach) {
                                this.additionalInformationText =
                                    ADDITIONAL_INFORMATION_MAP.FAILED_PENDING_TERMINATE.ACH;
                            }

                            this.paymentAmount = formatAsDollarsWithoutZeroCentsOrEmptyString(
                                this.policyInfo.next_unpaid_payment_amount
                            );

                            this.paymentStatusBreakdownItem = {
                                date: this.policyInfo.next_unpaid_payment_due_date,
                                value: this.paymentAmount,
                                pastDue: true
                            };

                            this.canOnlyPayLastFailedPaymentOption = true;
                            this.showPayNowBasedOnStatus = true;
                        } else {
                            // if next unpaid payment info is missing, last charge was successful
                            if (this.paymentMethod === paymentMethodDisplayName.cc) {
                                this.additionalInformationText =
                                    ADDITIONAL_INFORMATION_MAP.NON_PAYMENT_PENDING_TERMINATE_LAST_CHARGE_DID_NOT_FAIL.CC;
                            }

                            if (this.paymentMethod === paymentMethodDisplayName.ach) {
                                this.additionalInformationText =
                                    ADDITIONAL_INFORMATION_MAP.NON_PAYMENT_PENDING_TERMINATE_LAST_CHARGE_DID_NOT_FAIL.ACH;
                            }
                            this.paymentAmount = '';
                        }
                        this.cancellationDate = this.policyInfo.update_date;
                    } else {
                        setPaymentStatus('Payment Failed', PAYMENT_STATUS_TYPES.ERROR);
                        // This status should only apply for failed credit card payments.
                        this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.FAILED.CC;
                        this.showAutoRenewal = true;
                        this.renewalDate = getRenewalDate(this.policyInfo);
                        this.showPayNowBasedOnStatus = true;

                        // payment failed but policy is not yet pending cancellation
                        this.paymentAmount = formatAsDollarsWithoutZeroCentsOrEmptyString(
                            this.policyInfo.payment_amount
                        );

                        this.paymentStatusBreakdownItem = {
                            date: this.policyInfo.payment_due_date,
                            value: this.paymentAmount,
                            pastDue: true
                        };
                    }

                    break;
                case CUSTOMER_PAYMENT_STATUS.NOT_RECEIVED:
                    // Not received payment status for escrow applies to non-payment cancellations
                    if (this.paymentMethod === paymentMethodDisplayName.escrow) {
                        setPaymentStatus('Payment Failed (Pending Cancellation)', PAYMENT_STATUS_TYPES.ERROR);

                        const paymentDueDate = getMomentFromHippoDateString(this.policyInfo.effective_date)
                            .add(45, 'days')
                            .format(AMERICAN_DATE_FORMAT);
                        this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.NOT_RECEIVED.ESCROW(paymentDueDate);

                        this.cancellationDate = this.policyInfo.update_date;
                        // TODO: Add pay now functionality when backend can handle pay now for escrow policies pending cancellation due to non-payment
                    }

                    // Not received payment status for ach applies to past due charges that are not failed payments
                    if (this.paymentMethod === paymentMethodDisplayName.ach) {
                        setPaymentStatus('Payment not received', PAYMENT_STATUS_TYPES.ERROR);
                        this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.NOT_RECEIVED.ACH;

                        this.showPayNowBasedOnStatus = true;
                    }
                    this.paymentStatusBreakdownItem = {
                        date: this.policyInfo.payment_due_date,
                        value: formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount),
                        pastDue: true
                    };
                    break;
                case CUSTOMER_PAYMENT_STATUS.DUE:
                    setPaymentStatus('Outstanding Balance', PAYMENT_STATUS_TYPES.ERROR);
                    this.paymentStatusBreakdownItem = {
                        date: this.policyInfo.payment_due_date,
                        value: formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount),
                        pastDue: true
                    };

                    this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.DUE(
                        formatAsDollarsWithoutZeroCentsOrEmptyString(this.policyInfo.payment_amount)
                    );
                    this.showPayNowBasedOnStatus = true;
                    break;
                case CUSTOMER_PAYMENT_STATUS.CANCELLED:
                    setPaymentStatus('Policy Cancelled', PAYMENT_STATUS_TYPES.ERROR);
                    this.additionalInformationText = ADDITIONAL_INFORMATION_MAP.CANCELLED;
                    this.cancellationDate = this.policyInfo.update_date;
                    break;
            }
        };

        const updateDisplay = () => {
            this.paymentMethod = paymentMethodDisplayName[this.policyInfo.checkout_data.payment_method];
            this.canOnlyPayLastFailedPaymentOption = false;
            setPaymentStatusAndBreakdownItemsAndAdditionalInformation();
        };

        this.$onInit = () => {
            updateDisplay();
        };

        this.$onChanges = (changesObj) => {
            const policyInfo = changesObj.policyInfo;
            if (policyInfo && policyInfo.currentValue !== undefined) {
                $timeout(() => {
                    this.policyInfo = policyInfo.currentValue;
                    updateDisplay();
                });
            }
        };

        this.isEscrowSelected = () => {
            return this.paymentMethod === paymentMethodDisplayName.escrow;
        };

        this.isCreditCardSelected = () => {
            return this.paymentMethod === paymentMethodDisplayName.cc;
        };

        this.isAchSelected = () => {
            return this.paymentMethod === paymentMethodDisplayName.ach;
        };
    }
});

export { module };
